// The arithmetic behind the Members & Families summary. Pure functions over the
// light member and family lists (no photos, no documents), so the screen only
// lays the answers out.

import { countBy, lastMonths, dateOf, isoOf, yearsSince, daysUntil, inYear, percent } from '../../utils/summaryStats';
import type { NamedCount, MonthBucket } from '../../utils/summaryStats';

export interface CongregationMemberLike {
  memberId?: string;
  name?: string;
  familyId?: string;
  relationToHead?: string;
  gender?: string;
  dateOfBirth?: string;
  maritalStatus?: string;
  /** Alive / Deceased / Transferred — blank is Alive. */
  status?: string;
  /** Regular / Associate / Guest… — the free-form membership category. */
  memberStatus?: string;
  membershipDate?: string;
  guestFrom?: string;
  guestUntil?: string;
  guestOrigin?: string;
  baptismDate?: string;
  confirmationDate?: string;
  communionDate?: string;
  communionStatus?: string;
  recordStatus?: string;
}

export interface CongregationFamilyLike {
  familyId?: string;
  familyName?: string;
  headOfFamily?: string;
  contact?: string;
  /** ACTIVE / TRANSFERRED / INACTIVE — blank is ACTIVE. */
  status?: string;
  recordStatus?: string;
}

const isLive = (r: { recordStatus?: string }) => r.recordStatus !== 'VOID';
const has = (v?: string) => Boolean(v && String(v).trim());

/** Life status, blank read as Alive and the case tidied (seeded rows vary). */
export const lifeStatusOf = (m: CongregationMemberLike): string => {
  const s = String(m.status || '').trim().toLowerCase();
  return s === 'deceased' ? 'Deceased' : s === 'transferred' ? 'Transferred' : s === 'inactive' ? 'Inactive' : 'Alive';
};

/** Family status in the upper-case form the Heads tab uses; blank is ACTIVE. */
export const familyStatusOf = (f: CongregationFamilyLike): string =>
  String(f.status || 'ACTIVE').trim().toUpperCase() || 'ACTIVE';

export const isGuestMember = (m: CongregationMemberLike): boolean =>
  String(m.memberStatus || '').trim().toLowerCase() === 'guest';

const isHead = (m: CongregationMemberLike) => String(m.relationToHead || '').trim().toLowerCase() === 'head';

/** Age bands a parish plans around: Sunday School, youth, adults, seniors. */
export const AGE_BANDS: Array<{ name: string; from: number; to: number }> = [
  { name: 'Children (0–12)', from: 0, to: 12 },
  { name: 'Youth (13–19)', from: 13, to: 19 },
  { name: 'Young Adults (20–39)', from: 20, to: 39 },
  { name: 'Adults (40–59)', from: 40, to: 59 },
  { name: 'Seniors (60+)', from: 60, to: Infinity }
];

/** Stays ending within this many days are flagged — the Guests tab's threshold. */
const GUEST_WARNING_DAYS = 30;

export interface GuestWatch extends CongregationMemberLike { days: number; }

export interface FamilyWatch extends CongregationFamilyLike { members: number; }

export interface CongregationSummary {
  familiesTotal: number;
  familiesByStatus: NamedCount[];
  activeFamilies: number;
  /** Active families by how many living members they hold. */
  householdSizes: NamedCount[];
  avgHousehold: number | null;
  /** Live (not voided) members of every life status. */
  membersTotal: number;
  byLifeStatus: NamedCount[];
  /** Alive and not a guest — the roll, as the Members tab counts it. */
  onRoll: number;
  guests: number;
  inactive: number;
  deceased: number;
  transferred: number;
  /** Alive members (roll and guests) by membership category. */
  byMemberStatus: NamedCount[];
  genders: NamedCount[];
  ageBands: NamedCount[];
  marital: NamedCount[];
  joinedThisYear: number;
  joinedMonths: MonthBucket[];
  joinedYears: NamedCount[];
  sacraments: Array<{ name: string; count: number; share: number }>;
  /** Guests whose stay has ended or ends within 30 days, soonest first. */
  guestWatch: GuestWatch[];
  /** Active families with no living member marked Head. */
  headless: FamilyWatch[];
  /** Living members whose family id matches no live family. */
  unlinked: CongregationMemberLike[];
}

export const congregationSummary = (
  members: CongregationMemberLike[], families: CongregationFamilyLike[], now: Date = new Date(), years = 6
): CongregationSummary => {
  const liveMembers = members.filter(isLive);
  const liveFamilies = families.filter(isLive);
  const alive = liveMembers.filter((m) => lifeStatusOf(m) === 'Alive');
  const roll = alive.filter((m) => !isGuestMember(m));
  const guests = alive.filter(isGuestMember);

  // Living members per family, and which families have a living head.
  const sizeOf = new Map<string, number>();
  const headed = new Set<string>();
  alive.forEach((m) => {
    const id = String(m.familyId || '').trim();
    if (!id) return;
    sizeOf.set(id, (sizeOf.get(id) || 0) + 1);
    if (isHead(m)) headed.add(id);
  });

  const active = liveFamilies.filter((f) => familyStatusOf(f) === 'ACTIVE');
  const sizes = active.map((f) => sizeOf.get(String(f.familyId || '')) || 0);
  const sizeBand = (n: number) => (n >= 5 ? '5 or more' : n === 0 ? 'No living members' : `${n} member${n === 1 ? '' : 's'}`);
  const sizeOrder = ['1 member', '2 members', '3 members', '4 members', '5 or more', 'No living members'];
  const householdSizes = sizeOrder
    .map((name) => ({ name, count: sizes.filter((n) => sizeBand(n) === name).length }))
    .filter((r) => r.count > 0);
  const peopled = sizes.filter((n) => n > 0);

  const ageBands = [
    ...AGE_BANDS.map((b) => ({
      name: b.name,
      count: alive.filter((m) => { const a = yearsSince(m.dateOfBirth, now); return a !== null && a >= b.from && a <= b.to; }).length
    })),
    { name: 'Age not recorded', count: alive.filter((m) => yearsSince(m.dateOfBirth, now) === null).length }
  ].filter((r, i, all) => r.count > 0 || i < all.length - 1);

  // Growth: the roll date of everyone still alive and live; future dates ignored.
  const today = isoOf(now);
  const dated = alive.filter((m) => { const d = dateOf(m.membershipDate); return d !== null && d <= today; });
  const thisYear = now.getFullYear();
  const joinedYears: NamedCount[] = [];
  for (let y = thisYear - years + 1; y <= thisYear; y++) {
    joinedYears.push({ name: String(y), count: inYear(dated, (m) => m.membershipDate, y).length });
  }

  const baptised = roll.filter((m) => has(m.baptismDate)).length;
  const communed = roll.filter((m) => has(m.communionDate) || m.communionStatus === 'Received').length;
  const confirmed = roll.filter((m) => has(m.confirmationDate)).length;

  const guestWatch = guests
    .map((m) => ({ ...m, days: daysUntil(m.guestUntil, now) }))
    .filter((m): m is GuestWatch => m.days !== null && m.days <= GUEST_WARNING_DAYS)
    .sort((a, b) => a.days - b.days);

  const familyIds = new Set(liveFamilies.map((f) => String(f.familyId || '').trim()).filter(Boolean));

  return {
    familiesTotal: liveFamilies.length,
    familiesByStatus: countBy(liveFamilies, familyStatusOf),
    activeFamilies: active.length,
    householdSizes,
    avgHousehold: peopled.length
      ? Math.round((peopled.reduce((s, n) => s + n, 0) / peopled.length) * 10) / 10
      : null,
    membersTotal: liveMembers.length,
    byLifeStatus: countBy(liveMembers, lifeStatusOf),
    onRoll: roll.length,
    guests: guests.length,
    inactive: liveMembers.filter((m) => lifeStatusOf(m) === 'Inactive').length,
    deceased: liveMembers.filter((m) => lifeStatusOf(m) === 'Deceased').length,
    transferred: liveMembers.filter((m) => lifeStatusOf(m) === 'Transferred').length,
    byMemberStatus: countBy(alive, (m) => m.memberStatus),
    genders: countBy(alive, (m) => m.gender),
    ageBands,
    marital: countBy(alive, (m) => m.maritalStatus),
    joinedThisYear: inYear(dated, (m) => m.membershipDate, thisYear).length,
    joinedMonths: lastMonths(dated, (m) => m.membershipDate, 12, now),
    joinedYears,
    sacraments: [
      { name: 'Baptised', count: baptised, share: percent(baptised, roll.length) },
      { name: 'First Communion', count: communed, share: percent(communed, roll.length) },
      { name: 'Confirmed', count: confirmed, share: percent(confirmed, roll.length) }
    ],
    guestWatch,
    headless: active
      .filter((f) => !headed.has(String(f.familyId || '').trim()))
      .map((f) => ({ ...f, members: sizeOf.get(String(f.familyId || '')) || 0 })),
    // With no families loaded (a failed fetch) every member would look orphaned,
    // which is noise rather than a finding.
    unlinked: familyIds.size ? alive.filter((m) => !familyIds.has(String(m.familyId || '').trim())) : []
  };
};
